import { useCookiesManager } from '@shared/apollo/useCookiesManager';
import { useMeResponse } from '@store/app/me';
import { useMeQuery } from '@store/entities/me/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';

// Module scope on purpose: logout() → clearAll() → resetJotaiStore() remounts
// the whole app, wiping component state. If the same access token then fails
// the boot check again (e.g. a cookie clearAll couldn't remove), rechecking it
// would remount the app again, forever. Remembering the failed token here
// breaks that cycle; a real page load resets it.
let lastFailedBootToken: string | null = null;

export const useAuth = () => {
  const { me: meQuery, meLoading } = useMeQuery();
  const { setMe } = useMeResponse();
  const { getAccessToken, hasAccessToken, clearAll, setAccessToken, setRefreshToken } =
    useCookiesManager;
  const [isSignedIn, setIsSignedIn] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true); // Start with loading true
  const [hasMerchantAccount, setHasMerchantAccount] = useState<boolean>();
  const authChecked = useRef(false);

  const checkMe = useCallback(async () => {
    try {
      const { res } = await meQuery();

      if (!res) {
        lastFailedBootToken = getAccessToken();
        await logout();
      } else {
        setIsSignedIn(true);
        setMe(res);
        setHasMerchantAccount(!!res.organizationId);
      }
      // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
    } catch (error) {
      //TODO: Should show something went wrong error
      lastFailedBootToken = getAccessToken();
      await logout();
    } finally {
      // Only set loading to false after auth check completes
      setLoading(false);
    }
  }, [meQuery]);

  // checkMe changes identity on every Apollo render; read it through a ref so the
  // boot effect below never re-runs (its cleanup would cancel the pending timeout
  // and keep pushing the auth check back while the app is still authenticating).
  const checkMeRef = useRef(checkMe);

  checkMeRef.current = checkMe;

  // Initialize auth state on mount
  useEffect(() => {
    const authCheck = async () => {
      if (!authChecked.current) {
        authChecked.current = true;

        if (hasAccessToken() && getAccessToken() !== lastFailedBootToken) {
          setIsSignedIn(true);
          await checkMeRef.current();
        } else {
          // No token (or a token that already failed this boot), not authenticating
          setLoading(false);
        }
      }
    };

    const timeoutId = setTimeout(() => {
      void authCheck(); // Explicitly void the promise
    }, 200);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [hasAccessToken, getAccessToken]);

  const logout = async () => {
    clearAll();
    setIsSignedIn(false);
    setHasMerchantAccount(undefined);
  };

  const setTokens = async (accessToken: string, refreshToken: string) => {
    setAccessToken(accessToken);
    setRefreshToken(refreshToken);
    setIsSignedIn(true);
  };

  const setUserHasMerchantAcccount = useCallback(
    (hasMerchantAccount: boolean) => {
      setHasMerchantAccount(hasMerchantAccount);
    },
    [setHasMerchantAccount]
  );

  const resolvingMerchantAccount = isSignedIn && hasMerchantAccount === undefined;

  return {
    // Auth State
    authenticated: isSignedIn,
    authenticating: loading || meLoading || resolvingMerchantAccount,
    accessToken: getAccessToken(),
    hasMerchantAccount,
    // Auth Actions
    checkMe,
    logout,
    setTokens,
    setUserHasMerchantAcccount,
  };
};
